/**
 * FilesCell — compact attachment strip for a table row (thumbs + pills + add).
 * Drag-drop a file onto the cell to upload it to that row.
 */

import React, { useRef, useState } from 'react';
import { PlusIcon } from '@heroicons/react/24/outline';
import type { BoardAttachment } from './types';
import { ATTACH_META } from './boardHelpers';
import { ThumbImg } from './FileDrawer';

export const FilesCell: React.FC<{
  boardId: string;
  attachments: BoardAttachment[];
  onOpen: (a: BoardAttachment) => void;
  onUpload: (files: FileList) => void;
  uploading?: boolean;
}> = ({ boardId, attachments, onOpen, onUpload, uploading }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);
  const docs = attachments || [];
  const shown = docs.slice(0, 3);
  const extra = docs.length - shown.length;

  return (
    <div
      className={`bd-files ${over ? 'bd-files--over' : ''}`}
      onDragOver={e => { if (e.dataTransfer.types.includes('Files')) { e.preventDefault(); setOver(true); } }}
      onDragLeave={e => { if (!e.currentTarget.contains(e.relatedTarget as Node)) setOver(false); }}
      onDrop={e => { if (e.dataTransfer.files.length) { e.preventDefault(); setOver(false); onUpload(e.dataTransfer.files); } }}
    >
      <input ref={inputRef} type="file" multiple hidden
        onChange={e => { if (e.target.files?.length) onUpload(e.target.files); e.target.value = ''; }} />

      {docs.length === 0 && !uploading && (
        <span className="bd-files-empty">Glissez un fichier ici</span>
      )}
      {uploading && <span className="bd-files-empty">Envoi…</span>}

      {shown.map(f => {
        const meta = ATTACH_META[f.kind] || ATTACH_META.file;
        const Icon = meta.Icon;
        if (f.kind === 'photo') {
          return (
            <button key={f.id} className="bd-file-thumb" onClick={() => onOpen(f)} title={f.name}>
              <ThumbImg boardId={boardId} att={f} />
            </button>
          );
        }
        return (
          <button key={f.id} className="bd-file-pill" onClick={() => onOpen(f)} title={f.name}>
            <span className={`bd-file-ic ${meta.cls}`}><Icon className="w-3 h-3" /></span>
            <span className="bd-file-nm">{f.name}</span>
          </button>
        );
      })}
      {extra > 0 && (
        <button className="bd-file-pill bd-file-pill--more" onClick={() => onOpen(docs[3])}>
          +{extra}
        </button>
      )}
      <button className="bd-file-add" onClick={() => inputRef.current?.click()} title="Joindre un fichier">
        <PlusIcon className="w-4 h-4" />
      </button>
    </div>
  );
};
